import React from 'react'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { IconHelp, IconLogout, IconMoon, IconSun } from '@tabler/icons-react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  Anchor,
  AppShell,
  Burger,
  Loader,
  Flex,
  Text,
  Title,
  ActionIcon,
  Divider,
  useMantineColorScheme,
  Stack,
  Group,
  ScrollArea,
} from '@mantine/core'

import { UserType } from '@customTypes/user'

import DataRepo from '@api/datasource/data'

import { useStoreBase } from '@store/index'

import { $ } from '@utils/styles'
import { Logger } from '@utils/log'
import { ErrorService } from '@utils/error'
import { transitionView } from '@utils/viewTransition'

import QueryKeys from '@constants/queryKeys'
import { RoutesApp } from '@constants/routes'

import TextIcon from '@components/shared/textIcon'
import { modals } from '@mantine/modals'
import { WEB_VERSION } from '@constants/app'
import { notifications } from '@mantine/notifications'
import { buildItemsNavBar } from '@utils/drawer'

export const HEIGHT_DRAWER = 50

export default function Protected() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [sectionSelected, setSectionSelected] = React.useState<string>('')

  const { user, setUser, reset: resetBase } = useStoreBase((state) => state)

  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure(false)
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true)

  const isMediaLowerLg = useMediaQuery('(max-width: 1024px)')

  const { toggleColorScheme, colorScheme } = useMantineColorScheme({ keepTransitions: true })

  const userQuery = useQuery<UserType | null, ErrorService>({
    queryKey: [QueryKeys.GET_USER_KEY],
    queryFn: async () => {
      const response = await DataRepo.userService.getUser()
      return response
    },
  })

  React.useEffect(
    () => {
      setSectionSelected(location.pathname.split('/')[1] ?? 'home')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname],
  )

  React.useEffect(() => {
    queryClient.invalidateQueries({
      exact: true,
      refetchType: 'all',
      queryKey: [QueryKeys.GET_USER_KEY],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if ([RoutesApp.LOGIN, RoutesApp.REGISTER].includes(location.pathname)) return

    if ((userQuery.isSuccess || userQuery.isError) && !userQuery.data) {
      Logger.info('Drawer: No user found, redirecting to login')
      notifications.show({
        color: 'blue',
        title: 'Sesión terminada',
        message: 'Por favor inicie sesión nuevamente',
      })
      navigate(RoutesApp.LOGIN)
    } else if (userQuery.data) {
      setUser(userQuery.data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userQuery.isPending, userQuery.data, userQuery.isSuccess, userQuery.isError])

  const NavBarMemo = React.useMemo(
    buildNavBar,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accentSelected, user?.role, sectionSelected, isMediaLowerLg, toggleMobile],
  )

  if (!user) {
    return (
      <Flex align="center" direction="column" gap="md" h="100vh" justify="center" wrap="wrap">
        <Loader color="blue" size="lg" type="dots" />
        <Text size="lg">Cargando su información, por favor espere</Text>
      </Flex>
    )
  }

  return (
    <AppShell
      header={{ height: HEIGHT_DRAWER }}
      navbar={{
        width: 255,
        breakpoint: 'lg',
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <div className="cd-h-full cd-flex cd-justify-between cd-items-center cd-px-[1rem]">
          <Flex align="center" gap="xs" justify="flex-start">
            <Burger hiddenFrom="lg" opened={mobileOpened} size="sm" onClick={toggleMobile} />
            <Burger opened={desktopOpened} size="sm" visibleFrom="lg" onClick={toggleDesktop} />
            <Link
              className={$('home-link-transition')}
              to={RoutesApp.HOME}
              onClick={(e) => {
                e.preventDefault()
                transitionView(() => navigate(RoutesApp.HOME))
                if (isMediaLowerLg) toggleMobile()
              }}
            >
              <Title className="cd-title-form" key="main-title" order={3}>
                Sein
                <Text inherit c="blue" component="span">
                  pro
                </Text>
              </Title>
            </Link>
          </Flex>
          <Flex align="center" gap="md" justify="flex-end">
            <ActionIcon
              variant="transparent"
              onClick={() => {
                if (document.startViewTransition) {
                  document.startViewTransition(toggleColorScheme)
                } else {
                  toggleColorScheme()
                }
              }}
            >
              {colorScheme === 'dark' && <IconSun size={24} />}
              {colorScheme === 'light' && <IconMoon size={24} />}
              {colorScheme === 'auto' && <IconMoon size={24} />}
            </ActionIcon>
            <ActionIcon variant="transparent" onClick={handleHelpModal}>
              <IconHelp size={24} />
            </ActionIcon>
          </Flex>
        </div>
      </AppShell.Header>
      <AppShell.Navbar pt={8} title="Menú">
        {NavBarMemo}
      </AppShell.Navbar>
      <AppShell.Main className="cd-h-full cd-flex cd-grow cd-flex-col cd-pb-0" pt={HEIGHT_DRAWER}>
        {React.cloneElement(<Outlet />, { className: 'cd-w-full' })}
      </AppShell.Main>
    </AppShell>
  )

  function buildNavBar() {
    const items = buildItemsNavBar(user).map((item) => (
      <React.Fragment key={item.path}>
        <Link
          className={$(
            'hover:cd-bg-blue-100 hover:!cd-text-blue-900 cd-transition-colors cd-duration-300 cd-ease-in-out',
            'cd-py-[0.75rem] cd-px-2 cd-mx-2 cd-rounded-md',
            accentSelected(item.path),
          )}
          to={item.path}
          onClick={() => {
            if (isMediaLowerLg) toggleMobile()
            navigate(item.path)
          }}
        >
          <TextIcon leftIcon={item.icon}>
            <Text>{item.label}</Text>
          </TextIcon>
        </Link>
        <Divider />
      </React.Fragment>
    ))

    items.push(
      <div
        className="cd-py-[0.75rem] cd-px-2 cd-mx-2 cd-rounded-md hover:cd-bg-red-200 hover:!cd-text-red-800 cd-transition-colors cd-duration-300 cd-ease-in-out"
        key="logout-button"
      >
        <TextIcon
          className="cd-cursor-pointer"
          key="logout"
          leftIcon={<IconLogout className="cd-text-red-500 hover:!cd-text-red-800" />}
          onClick={reset}
        >
          <Text>Cerrar sesión</Text>
        </TextIcon>
      </div>,
    )

    return (
      <ScrollArea h="100%" type="auto">
        <div className="cd-flex cd-flex-col cd-gap-y-2">{items}</div>
      </ScrollArea>
    )
  }

  function accentSelected(path: string) {
    return path.includes(sectionSelected) ? '!cd-text-blue-600 dark:!cd-text-blue-300' : ''
  }

  function reset() {
    DataRepo.userService.logout().finally(() => {
      resetBase()
      localStorage.clear()
      navigate(RoutesApp.LOGIN)
    })
  }

  function handleHelpModal() {
    modals.open({
      centered: true,
      title: 'Soporte',
      size: 'lg',
      children: (
        <Stack gap="md">
          <Text>
            Si tienes alguna duda, puedes contactarme a través de mi correo electrónico{' '}
            <Anchor href="mailto:enmanuelmag@cardor.dev">enmanuelmag@cardor.dev</Anchor>
          </Text>

          <Group className="cd-mt-[1rem] cd-text-sm" justify="end">
            <Text c="dimmed">Version: {WEB_VERSION}</Text>
          </Group>
        </Stack>
      ),
    })
  }
}
